import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFullscreenElement } from '../utils/videoPlayer.utils';

const useVideoFullscreen = (widgetRuntime) => {
  const fullscreenRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenAvailable = widgetRuntime?.supportsFullscreen !== false;
  const getFullscreenDocument = useCallback(
    () =>
      fullscreenRef.current?.ownerDocument || widgetRuntime?.runtimeWindow?.document || document,
    [widgetRuntime?.runtimeWindow],
  );

  const syncFullscreenState = useCallback(() => {
    setIsFullscreen(getFullscreenElement(getFullscreenDocument()) === fullscreenRef.current);
  }, [getFullscreenDocument]);

  useEffect(() => {
    const fullscreenDocument = getFullscreenDocument();
    fullscreenDocument.addEventListener('fullscreenchange', syncFullscreenState);
    fullscreenDocument.addEventListener('webkitfullscreenchange', syncFullscreenState);
    fullscreenDocument.addEventListener('MSFullscreenChange', syncFullscreenState);

    return () => {
      fullscreenDocument.removeEventListener('fullscreenchange', syncFullscreenState);
      fullscreenDocument.removeEventListener('webkitfullscreenchange', syncFullscreenState);
      fullscreenDocument.removeEventListener('MSFullscreenChange', syncFullscreenState);
    };
  }, [getFullscreenDocument, syncFullscreenState]);

  const enterFullscreen = useCallback(async () => {
    const element = fullscreenRef.current;
    if (!element || !fullscreenAvailable) return;

    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      } else {
        console.warn('Fullscreen API is not supported in this browser.');
      }
    } catch (error) {
      console.error('Error attempting to enter fullscreen mode:', error);
    }

    syncFullscreenState();
  }, [fullscreenAvailable, syncFullscreenState]);

  const exitFullscreen = useCallback(async () => {
    const fullscreenDocument = getFullscreenDocument();
    try {
      if (fullscreenDocument.exitFullscreen) {
        await fullscreenDocument.exitFullscreen();
      } else if (fullscreenDocument.webkitExitFullscreen) {
        fullscreenDocument.webkitExitFullscreen();
      } else if (fullscreenDocument.msExitFullscreen) {
        fullscreenDocument.msExitFullscreen();
      }

      syncFullscreenState();
    } catch (error) {
      console.error('Could not exit fullscreen:', error);
    }
  }, [getFullscreenDocument, syncFullscreenState]);

  const toggleFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await exitFullscreen();
      return;
    }

    await enterFullscreen();
  }, [enterFullscreen, exitFullscreen, isFullscreen]);

  return useMemo(
    () => ({
      fullscreenRef,
      fullscreenAvailable,
      isFullscreen,
      toggleFullscreen,
      fullscreenLabel: !fullscreenAvailable
        ? 'Fullscreen is unavailable in Picture-in-Picture'
        : isFullscreen
          ? 'Exit fullscreen'
          : 'Enter fullscreen',
    }),
    [fullscreenAvailable, isFullscreen, toggleFullscreen],
  );
};

export default useVideoFullscreen;
